'use client';

import { useTranslations } from 'next-intl';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import cx from 'classnames';
import styles from './add-to-cart-button.module.scss';
import { FC, useCallback } from 'react';
import { useCoreContext } from 'providers/core-provider';
import { useProductDetailPageContext } from '../../context/product-detail';
import { useQuickToast } from 'hooks/use-toast';
import {
  ProductDealerPriceAvailability,
  DealerPriceAvailabilityResponse
} from 'services/dealerPriceAvailability/types';
import { ProductPriceAvailability } from 'services/priceAvailability/types';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import { getFormattedItemData } from 'components/product-comparison-grid/product-comparison-card/utils';
import { PRIME_PDP_KITS } from '../../constants/constants';
import { CartResponse } from 'services/cart/types';
import { getPartNumberForAddToCart } from 'services/cart/utils';

const getValueAndCheck = (value?: string) => value || '';

const checkSize = (isMobileView: boolean) => (isMobileView ? 'md' : 'sm');
interface CartActionsProps {
  part?: ProductDealerPriceAvailability | ProductPriceAvailability;
  withMobileFixedActions?: boolean;
  isKit?: boolean;
  displayAddToListButton?: boolean;
  isMobileView?: boolean;
  currency?: string;
  priceAvailabilityData;
  cartDetails?: CartResponse;
  locationId?: string;
  dealerFluidAttributes?:
    | {
        fluidPartNumber: string;
        sosCode: string;
      }
    | Record<string, never>;
  priceAvailabilityDrawerData?: DealerPriceAvailabilityResponse | null;
}
const AddToCartButton: FC<CartActionsProps> = ({
  part,
  withMobileFixedActions = false,
  isKit = false,
  displayAddToListButton = false,
  isMobileView = false,
  currency,
  priceAvailabilityData,
  cartDetails,
  locationId,
  dealerFluidAttributes,
  priceAvailabilityDrawerData
}) => {
  const t = useTranslations();
  const { quantity, locale, storeId } = useProductDetailPageContext();
  const { handleAddToCart } = useCoreContext();
  const toast = useQuickToast();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();
  const itemListNameId = PRIME_PDP_KITS;
  const getPayload = useCallback(() => {
    if (priceAvailabilityDrawerData?.priceAndAvailability?.length === 0) {
      return [];
    }

    if (priceAvailabilityDrawerData?.priceAndAvailability?.length > 0) {
      return priceAvailabilityDrawerData.priceAndAvailability
        .filter((item) => item?.enableAddToCart && item?.quantity > 0)
        .map((item) => ({
          partNumber: item?.partNumber,
          quantity: item?.quantity,
          partName: getValueAndCheck(item?.partName),
          lineItemNote: item?.partNumber,
          sosCode: getValueAndCheck(item?.sosCode)
        }));
    }
    return [
      {
        partNumber: getPartNumberForAddToCart(
          part?.partNumber,
          part?.sosCode,
          part?.partLevelFlags?.isCatPart
        ),
        partName: getValueAndCheck(part?.partName),
        quantity,
        sosCode: part?.sosCode,
        isCatPart: part?.partLevelFlags?.isCatPart,
        ...(dealerFluidAttributes ? { ...dealerFluidAttributes } : {})
      }
    ];
  }, [part, quantity, dealerFluidAttributes, priceAvailabilityDrawerData]);

  const handleAddToCartClick = useCallback(async () => {
    const items = getPayload();
    if (!items || items.length === 0) {
      toast.error({
        message: t('ITEM_ADD_ERROR')
      });
      return;
    }
    const response = await handleAddToCart({ items }, storeId, locale);
    if (!response?.error) {
      toast.success({
        message: t('ITEM_ADDED_TO_CART')
      });
    } else {
      toast.error({
        message: t('ITEM_ADD_ERROR')
      });
    }
    addToCartItemEvent({
      locationId: storeId,
      itemSummary: priceAvailabilityData,
      itemData: getFormattedItemData(
        cartDetails?.orderHeader?.orderId,
        priceAvailabilityData,
        currency
      ),
      itemListId: itemListNameId,
      itemListName: itemListNameId
    });
  }, [
    addToCartItemEvent,
    currency,
    itemListNameId,
    locale,
    cartDetails,
    priceAvailabilityData,
    storeId,
    toast,
    t,
    handleAddToCart,
    getPayload
  ]);
  return withMobileFixedActions ? (
    <div
      className={cx({
        'px-1 d-md-inline ': displayAddToListButton,
        'd-block d-md-none': isMobileView,
        'd-none d-md-inline-block': !isMobileView
      })}
    >
      <CatButton
        data-testid="AddKitToCartButton"
        size={checkSize(isMobileView)}
        variant="primary"
        onClick={handleAddToCartClick}
        disabled={quantity < 1}
        fullWidth
      >
        {t('ADD_TO_CART')}
      </CatButton>
    </div>
  ) : (
    <div
      className={cx({
        'd-flex align-items-start px-md-3 px-lg-3': isKit,
        'd-flex align-items-start': !isKit
      })}
    >
      <div
        className={cx('d-inline-block', {
          [styles['cart-item-button-kit__container']]: isKit,
          [styles['cart-item-button__container']]: !isKit
        })}
      >
        <div className="d-none d-md-block">
          <CatButton
            data-testid="AddKitToCartButtonDesktop"
            size="sm"
            variant="primary"
            onClick={handleAddToCartClick}
            disabled={quantity < 1}
          >
            {t('ADD_TO_CART')}
          </CatButton>
        </div>
        {!withMobileFixedActions && (
          <div className="d-md-none">
            <CatButton
              data-testid="AddKitToCartButtonMobile"
              size="md"
              variant="primary"
              onClick={handleAddToCartClick}
              disabled={quantity < 1}
            >
              {t('ADD_TO_CART')}
            </CatButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddToCartButton;
